import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import client from '@js/modules/httpClient/index';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import { InvestorRegistrationContainer } from '@js/components/common/investorRegistrations/InvestorRegistrationContainer';
import { Modal } from '@js/components/corporateRegistrations/bankAccount/Modal';
import { BankNameInitials } from '@js/components/corporateRegistrations/bankAccount/BankNameInitials';
import { BankNameSelect } from '@js/components/corporateRegistrations/bankAccount/BankNameSelect';
import { BranchNameInitials } from '@js/components/corporateRegistrations/bankAccount/BranchNameInitials';
import { BranchNameSelect } from '@js/components/corporateRegistrations/bankAccount/BranchNameSelect';
import { BankAccount, BankAccountFormData } from '@js/interfaces';
import { BankAccountValidation } from '@js/formSettings/validations';
import { usePath } from '@js/hooks/usePath';
import { Context } from '@js/routes';
import { CONFIRM_MESSAGE } from '@js/components/corporateRegistrations/constants';
import Toast from '@js/modules/toast';
import { SingleInputUnit } from '@js/components/FormUnits/SingleInputUnit';
import { InputField } from '../FormUnits/InputField';
import { convertToLargeKana } from '@js/modules/kanaConversion';

type BankItem = {
  id: number
  code: string
  name: string
  name_hira: string
};

export const CorporateBankAccount = () => {
  const { setIsLoading, isEdit, setIsEdit } = useContext(Context);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const routeNumList = { bankInitials: 0, bankSelect: 1, branchInitials: 2, branchSelect: 3 };
  const [routeNum, setRouteNum] = useState(routeNumList.bankInitials);
  const [bank_account, set_bank_account] = useState<BankAccount>({
    bank_id: null,
    bank_name: '',
    branch_id: null,
    branch_name: '',
    number: '',
    holder_name: '',
    account_type: '',
  });
  const [bank_list, set_bank_list] = useState<BankItem[]>([]);
  const [branch_list, set_branch_list] = useState<BankItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [uploadedFileErrorText, setUploadedFileErrorText] = useState('');
  const methods = useForm<BankAccountFormData>({
    shouldUnregister: false
  });
  const { register, watch, setError, formState: { errors }, setValue, control } = methods;
  const { currentStep, pushNext } = usePath(isEdit);
  const watchBankInitial = watch('kana_table_bank');
  const watchRadioButtonBank = watch('radio_button_bank');
  const watchRadioButtonBranch = watch('radio_button_branch');
  const handleHolderNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const upperCaseValue = convertToLargeKana(e.target.value);
    setValue('holder_name',upperCaseValue);
  };

  const onSubmit = (data: BankAccountFormData) => {
    // 修正モードの場合、確認ダイアログを表示
    if (isEdit && !confirm(CONFIRM_MESSAGE)) return;
  
    // サーバーに送信するためのFormDataを準備
    const formData = new FormData();
  
    // テキスト情報を追加
    formData.append('bank_account[holder_name]', data.holder_name);
    formData.append('bank_account[number]', data.number);
    formData.append('bank_account[account_type]', data.account_type);
  
    // branch_idが有効な値の場合のみ追加する
    if (data.branch_id) {
      formData.append('bank_account[branch_id]', data.branch_id);
    }
  
    // 新しい画像ファイルが選択されている場合のみ追加する
    if (data.image_data && data.image_data.length > 0) {
      formData.append('bank_account_image[image]', data.image_data[0], data.image_data[0].name);
    }
  
    // サーバーにデータを送信
    client.post('/api/v1/customer/investor_registration/bank_account', formData)
      .then((response) => {
        if (isEdit) {
          const message = response.data.message;
          Toast.setFlashNotice(message);
        }
        pushNext();
      })
      .catch((error) => {
        // エラーハンドリング
        // サーバーから返されたエラーメッセージを安全に取得
        const errorText = error.response?.data?.errors?.[0];
  
        // もしエラー内容が「画像」に関するものであれば、専用の場所にエラーメッセージを表示
        if (errorText && errorText.match(/画像/)) {
          setUploadedFileErrorText(errorText);
        } else {
          // それ以外のエラーは、開発者が気づけるようにコンソールに出力しておく
          console.error('Submission failed:', error.response || error);
        }
      });
  };

  useEffect(() => {
    setIsLoading(true);
    client.get('/api/v1/customer/investor_registration/bank_account')
      .then(response => {
        const data = response.data;
        set_bank_account({
          bank_id: data.branch.bank_id,
          bank_name: data.bank.name,
          branch_id: data.bank_account.branch_id,
          branch_name: data.branch.name,
          number: data.bank_account.number,
          holder_name: data.bank_account.holder_name,
          account_type: data.bank_account.account_type
        });
        if(data.bank_account_image.image.metadata) {
          setUploadedFileName(data.bank_account_image.image.metadata.filename);
        }
        setIsEdit(!data['registration_in_progress?']);
      })
      .finally(() => setIsLoading(false));
  }, []); // eslint-disable-line
  useEffect(() => {
    const formattedData = {...bank_account};
    const keys = Object.keys(formattedData) as (keyof typeof formattedData)[];
    keys.forEach((key) => {
      if(formattedData[key] != null) setValue(key, `${formattedData[key]}`);
    });
    setIsLoading(false);
  }, [bank_account, setValue, setIsLoading]);

  const onUpdateBankAccount = (key: 'bank_id' | 'branch_id', value: string) => {
    const bankAccountReplica = { ...bank_account };
    bankAccountReplica[key] = Number(value);
    if(key === 'bank_id') {
      const tempItem = bank_list.find(item => item.id === Number(value));
      bankAccountReplica['bank_name'] = tempItem ? tempItem.name : '';
      bankAccountReplica['branch_name'] = '';
      set_bank_account(bankAccountReplica);
      setRouteNum(routeNumList.branchInitials);
    }
    if(key === 'branch_id') {
      const tempItem = branch_list.find(item => item.id === Number(value));
      bankAccountReplica['branch_name'] = tempItem ? tempItem.name : '';
      set_bank_account(bankAccountReplica);
      setIsOpen(false);
    }
  };
  const onUpdateInitial = (label: 'bank' | 'branch') => {
    if(label === 'bank' && !watchBankInitial) {
      setError(`kana_table_${label}`, {
        type: 'manual',
        message: '頭文字を選択してください',
      });
      return;
    }
    const initial = watch(`kana_table_${label}`) || '';
    const encoded = encodeURI(initial);
    let url: string;
    let func: React.Dispatch<React.SetStateAction<BankItem[]>>;
    switch(label) {
      case 'bank':
        url = `/banks/?initial=${encoded}`;
        func = set_bank_list;
        break;
      case 'branch':
        url = `/banks/${bank_account.bank_id}/branches/?initial=${encoded}`;
        func = set_branch_list;
        break;
      default:
        return;
    }
    setIsModalLoading(true);
    client.get(url)
      .then(response => {
        func(response.data);
        const nextRouteNum = label === 'bank' ? routeNumList.bankSelect : routeNumList.branchSelect;
        setRouteNum(nextRouteNum);
      })
      .finally(() => setIsModalLoading(false));
  };

  return (
    <InvestorRegistrationContainer formId={currentStep.name} isEdit={isEdit} >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="form-wrap mt40" id={currentStep.name} >
          <div className="form-input__wrap">
            <label className="form-label" htmlFor="bank_name">
              <span className="form-label__required">必須</span>金融機関名
              {Boolean(bank_account.bank_id) && (
                <button onClick={() => {setRouteNum(routeNumList.bankInitials); setIsOpen(true);}} type="button" className="bank-account__retry">
                  <span className="material-icons bank-account__retry-icon">replay</span>
                  金融機関名を選択
                </button>
              )}
            </label>
            {!bank_account.bank_id && (
              <button
                onClick={() => setIsOpen(true)}
                type="button"
                className="button-short-base--line button-w182 button-w164"
              >
                金融機関を登録する
              </button>
            )}
            <input
              onClick={() => {setRouteNum(routeNumList.bankInitials); setIsOpen(true);}}
              {...register('bank_name', BankAccountValidation.bank)}
              type="text"
              id="bank_name"
              readOnly
              value={bank_account.bank_name}
              className={`form-input ${errors.bank_name && 'is-error'}`}
              placeholder="例）あおぞら銀行"
              style={bank_account.bank_id ? {display: 'inline-block'} : {display: 'none'}}
            />
            <ErrorMessage
              errors={errors}
              name="bank_name"
              render={({ message }) =>
                <span className="form-input__error">金融機関は{message}</span>}
            />
          </div>
          {Boolean(bank_account.bank_id) && (
            <>
              <div className="form-input__wrap">
                <label className="form-label" htmlFor="branch_name">
                  <span className="form-label__required">必須</span>支店名
                  <button onClick={() => {setRouteNum(routeNumList.branchInitials); setIsOpen(true);}} type="button" className="bank-account__retry">
                    <span className="material-icons bank-account__retry-icon">replay</span>
                    支店名を選択
                  </button>
                </label>
                <input
                  {...register('branch_name', BankAccountValidation.branch)}
                  onClick={() => {setRouteNum(routeNumList.branchInitials); setIsOpen(true);}}
                  type="text"
                  id="branch_name"
                  readOnly
                  value={bank_account.branch_name}
                  className={`form-input ${errors.branch_name && 'is-error'}`}
                  placeholder="例）新宿支店"
                />
                <ErrorMessage
                  errors={errors}
                  name="branch_name"
                  render={({ message }) =>
                    <span className="form-input__error">支店名は{message}</span>}
                />
              </div>
            </>
          )}
          {isOpen && (
            <Modal setIsOpen={setIsOpen} >
              {routeNum === 0 && (
                <BankNameInitials
                  onUpdateInitial={onUpdateInitial}
                  register={register}
                  errors={errors}
                />
              )}
              {routeNum === 1 && (
                <BankNameSelect
                  routeNumList={routeNumList}
                  setRouteNum={setRouteNum}
                  bank_list={bank_list}
                  register={register}
                  watchRadioButtonBank={watchRadioButtonBank}
                  onUpdateBankAccount={onUpdateBankAccount}
                  isModalLoading={isModalLoading}
                  errors={errors}
                />
              )}
              {routeNum === 2 && (
                <BranchNameInitials
                  routeNumList={routeNumList}
                  setRouteNum={setRouteNum}
                  onUpdateInitial={onUpdateInitial}
                  register={register}
                />
              )}
              {routeNum === 3 && (
                <BranchNameSelect
                  routeNumList={routeNumList}
                  setRouteNum={setRouteNum}
                  branch_list={branch_list}
                  register={register}
                  watchRadioButtonBranch={watchRadioButtonBranch}
                  onUpdateBankAccount={onUpdateBankAccount}
                  errors={errors}
                />
              )}
            </Modal>
          )}
          <div className="form-input__wrap">
            <label className="form-label" htmlFor="account_type_savings">
              <span className="form-label__required">必須</span>口座種別
            </label>
            <Controller
              name="account_type"
              control={control}
              rules={{ required: '選択してください' }}
              render={({ field, fieldState: { error } }) => (
                <div className="form-column">
                  <label className="form-radio__label mr32" htmlFor="account_type_savings">
                    <input
                      type="radio"
                      // エラー時
                      className={`form-radio__input ${error && 'is-error'}`}
                      value="savings"
                      onChange={() => field.onChange('savings')}
                      checked={field.value === 'savings'}
                      id="account_type_savings"
                      name={field.name}
                    />
                    <span className="form-radio__text">普通預金</span>
                  </label>
                  <label className="form-radio__label mr32" htmlFor="account_type_current">
                    <input
                      type="radio"
                      className={`form-radio__input ${error && 'is-error'}`}
                      value="current"
                      onChange={() => field.onChange('current')}
                      checked={field.value === 'current'}
                      id="account_type_current"
                      name={field.name}
                    />
                    <span className="form-radio__text">当座預金</span>
                  </label>
                </div>
              )}
            />
            <ErrorMessage
              errors={errors}
              name="account_type"
              render={({ message }) =>
                <span className="form-input__error">口座種別は{message}</span>}
            />
          </div>
          <div className="form-input__wrap">
            <label className="form-label" htmlFor="number">
              <span className="form-label__required">必須</span>口座番号
              <a className="form-label__link ml16" target="_blank" href="https://www.jp-bank.japanpost.jp/kojin/sokin/koza/kj_sk_kz_furikomi_ksk.html" rel="noopener noreferrer">
                <i className="material-icons form-label__link-icon">info</i>
                ゆうちょ銀行の登録方法
              </a>
            </label>
            <div className="form-column">
              <div className="form-column__item">
                <input
                  {...register('number', BankAccountValidation.number)}
                  type="tel"
                  id="number"
                  className={`form-input ${errors.number && 'is-error'}`}
                  placeholder="例）0398765"
                />
                <ErrorMessage
                  errors={errors}
                  name="number"
                  render={({ message }) =>
                    <span className="form-input__error">口座番号は{message}</span>}
                />
              </div>
            </div>
          </div>
          <InputField
            name="holder_name"
            label="口座名義"
          >
            <SingleInputUnit
              name="holder_name"
              required={true}
              label="口座名義"
              inputType="text"
              placeholder="例）ヤマダ　ハナコ"
              validation={BankAccountValidation.holderName}
              initialValue={bank_account.holder_name}
              onChange={handleHolderNameChange}
            />
            <ul className="form-input__note">
              <li>※全角カタカナ(ヰ・ヱ・ヲを除く)もしくは半角英字（大文字）でご入力ください。</li>
              <li>※小文字(ｧ・ｨ・ｪ・ｯ・ｬ・ｭ・ｮ等)は利用せず、大文字でご入力ください。</li>
              <li>※姓と名の間にはスペースをご入力ください。</li>
              <li>※記号は半角カッコ&quot;(&quot;もしくは&quot;)&quot;のみが利用できます。</li>
            </ul>
          </InputField>
          <div className="bg--gray form-file-block__wrap">
            <div className="form-file-block">
              <h3 className="form-file-block__heading">
                <span className="form-label__required">必須</span>画像のアップロード
              </h3>
              <div className="form-file-block__lead">通帳、キャッシュカード、ネットバンキングの画面いずれかの画像をアップロードしてください</div>
              <ul className="list-short-notes">
                <li className="list-short-notes__item">※アップロード可能なファイルの種類は「jpg / jpeg / png」のいずれかです</li>
                <li className="list-short-notes__item">※ファイルサイズは1枚5MB以下にしてください</li>
                <li className="list-short-notes__item">※ios11以降の端末で撮影した画像の場合、拡張子が「.HEIC」となりアップロードが行えない場合があります。<a target="_blank" rel="noopener" href="/pages/faq">よくあるご質問</a>「iPhoneで撮影した写真がアップロードできない場合」をご確認ください</li>
              </ul>
              <Controller
                control={control}
                name="image_data"
                rules={{
                  validate: {
                    isUploaded: (value) => Boolean(value && value[0]) || Boolean(uploadedFileName) || '必ずアップロードしてください'
                  }}}
                render={({field: { onChange, value }}) => (
                  <div className="form-file-block__button mt16">
                    <div className="form-input__wrap">
                      <div className="form-file__wrap">
                        <input
                          onChange={e => { if (e && e.target && e.target.files && e.target.files.length > 0) onChange(e.target.files); }}
                          className={`form-file form-file--corporate ${errors.image_data && 'is-error'}`}
                          type="file"
                          accept=".jpg, .png"
                          id="image_data"
                        />
                        <label className="button-short-base--line form-file__button" htmlFor="image_data">
                          ファイルを選択する
                        </label>
                        <div className="form-file__name">{(value && value[0].name) || uploadedFileName || '選択されていません'}</div>
                      </div>
                      <div className='form-input__error'>{uploadedFileErrorText}</div>
                      <ErrorMessage
                        errors={errors}
                        name="image_data"
                        render={({ message }) =>
                          <span className="form-input__error">画像は{message}</span>}
                      />
                    </div>
                  </div>
                )}
              />

            </div>
            <div className="form-file-block">
              <h3 className="form-file-block__heading">注意事項</h3>
              <div className="form-file-block__text mb16">下記の内容が記載されている画像をアップロードしてください</div>
              <ul className="list-base mb8">
                <li className="list-base__item">銀行名</li>
                <li className="list-base__item">支店番号</li>
                <li className="list-base__item">口座番号</li>
                <li className="list-base__item">口座名義（カタカナまたは英字）</li>
              </ul>
              <div className="form-file-block__text">クレジットカード一体型のキャッシュカードの場合は、クレジット番号を塗りつぶしてご提出ください。</div>
              <div className="form-file-block__example mb24">
                <div className="form-example">
                  <div className="form-example__body"><img alt="塗りつぶし箇所" src={`${import.meta.env.VITE_FINCF_APP_PUBLIC_CDN_HOST}/vite/img/img_credit_card-7401dfd1c8350ad3908821db15b23b08.jpg`} /></div>
                </div>
              </div>
              <ul className="list-short-notes">
                <li className="list-short-notes__item font-emphasis">※一部Webバンク等で発行されたキャッシュカードにおいて、確認に必要な情報が表面に記載されていない場合がございます。<br />お手数ですが銀行のWeb管理画面にて上記４点が確認できる画面キャプチャを取得し、アップロードをお願いします。</li>
              </ul>
            </div>
          </div>
        </form>
      </FormProvider>
    </InvestorRegistrationContainer>
  );
};
